import { Col, Row, Menu, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLang } from "../../features/language/language";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "../../shared/commonFunction";
import { activeStyle, menuItemStyle, menuStyle } from "../Navbar/NavbarStyles";
import footerLogo from "../../assets/images/footer-logo.svg";
import chatSupportIcon from "../../assets/icons/support.svg";
import facebook from "../../assets/icons/facebook.svg";
import tiktok from "../../assets/icons/tiktok.svg";
import instagram from "../../assets/icons/Instagram.svg";
import message from "../../assets/icons/message.svg";
import socialYoutube from "../../assets/icons/social-youtube.svg";
import twitter from "../../assets/icons/twitter.svg";
import "./Footer.scss";
import { fetchGamificationProgress } from "../../features/gamification/gamificationSlice";

const Footer = () => {
  const { t, i18n } = useTranslation("common");
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useSelector((state) => state.language.language);
  const isLogin = useSelector((state) => state.login.isLogin);
  const hasActiveEvents = useSelector((state) => state.event.hasActiveEvents);
  // eslint-disable-next-line no-unused-vars
  const [language, setLanguage] = useState(getLocalStorageData("language"));

  // Memoized data
  const menuItems = useMemo(() => [
    { key: "/", label: t("menu.home") },
    { key: "/all-tournaments", label: t("menu.tournaments") },
    { key: `/store`, label: t("menu.store") },
    { key: "/one-to-one", label: t("menu.one_to_one") },
    ...(hasActiveEvents ? [{ key: "/events", label: t("menu.events") }] : []),
    { key: "/about-us", label: t("menu.about_us") },
    { key: "/leadership-board", label: t("menu.leaderboard") },
    { key: "/join-our-community", label: t("menu.join_our_community") },
    { key: "/contact-us", label: t("menu.contact_us") },
  ], [t, hasActiveEvents]);

  const socialItems = useMemo(() => [
    { href: "https://x.com/FanzatyEsports", image: twitter, alt: "twitter" },
    { href: "https://www.youtube.com/@Fanzaty.esports", image: socialYoutube, alt: "youtube" },
    { href: "https://www.facebook.com/profile.php?id=61554300357539", image: facebook, alt: "facebook" },
    { href: "https://www.instagram.com/fanzaty.esports", image: instagram, alt: "instagram" },
    { href: "https://www.tiktok.com/@fanzaty.esports", image: tiktok, alt: "tiktok" },
    { href: "", image: message, alt: "message" },
  ], []);

  const isArabic = i18n.language === "ar";

  // Handlers
  const changeLanguage = useCallback((lng) => {
    setLocalStorageData("language", lng);
    i18n.changeLanguage(lng);
    document.body.dir = lng === "ar" ? "rtl" : "ltr";
    document.body.classList.toggle("font-arabic", lng === "ar");
    document.body.classList.toggle("font-english", lng !== "ar");
    dispatch(setLang(lng));
    setLanguage(lng);
  }, [dispatch, i18n]);

  const handleSupport = useCallback(() => {
    setLocalStorageData("isChatUser", "689dbca05b5f2fd658fd541d");
    navigate("/messages");
  }, [navigate]);

  // Effects
  useEffect(() => {
    const storedLanguage = getLocalStorageData("language");
    if (storedLanguage) {
      setLanguage(storedLanguage);
      dispatch(setLang(storedLanguage));
    }
  }, [dispatch]);

  // gamification related code - to be used in future when we want to show gamification related info in footer
  useEffect(() => {
    if (isLogin) {
      dispatch(fetchGamificationProgress());
    }
  }, [dispatch, isLogin, t]);

  // Derived values
  const showChatSupport = location.pathname !== "/messages" && isLogin;
  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  return (
    <section className={`footer-section ${isArabic ? "rtl" : "ltr"}`}>
      {showChatSupport && (
        <Tooltip title={t("footer.support")}>
          <div className="chat-support" onClick={handleSupport}>
            <img src={chatSupportIcon} alt="chat-support" loading="lazy" />
          </div>
        </Tooltip>
      )}

      <div className="container">
        <Row gutter={[16, 16]} justify="space-between" className="under-footer-section">
          <Col xs={24} sm={12} md={6} className="footer-logo">
            <Link to="/">
              <img src={footerLogo} alt="footer-logo" loading="lazy" />
            </Link>
          </Col>

          <Col xs={24} sm={12} md={12} className="footer-middle">
            <div
              className="footer-description"
              dangerouslySetInnerHTML={{ __html: t("footer.description") }}
            />

            <div className="footer-menu-icons">
              <Menu
                mode="horizontal"
                selectedKeys={selectedKeys}
                style={menuStyle}
                className="under-footer-menu-icons"
                items={menuItems.map((item) => ({
                  key: item.key,
                  label: <Link to={item.key}>{item.label}</Link>,
                  style: location.pathname === item.key ? activeStyle : menuItemStyle,
                }))}
              />
            </div>

            <div className="social-icons">
              {socialItems.map((item, index) => (
                <div key={index}>
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    <img src={item.image} alt={item.alt} loading="lazy" />
                  </a>
                </div>
              ))}
            </div>

            <div>
              <p
                className="copy-write"
                dangerouslySetInnerHTML={{ __html: t("footer.copyright") }}
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} className="footer-right">
            <div className="footer-login-buttons">
              {!isLogin && (
                <>
                  <Link to="/login" className="login-btn">
                    {t("buttons.login")}
                  </Link>
                  <Link to="/sign-up" className="sign-up-btn">
                    {t("buttons.sign_up")}
                  </Link>
                </>
              )}
              <span
                className={lang === "ar" ? "selected-language" : "language"}
                onClick={() => changeLanguage("ar")}
              >
                Ar
              </span>
              /
              <span
                className={lang === "en" ? "selected-language" : "language"}
                onClick={() => changeLanguage("en")}
              >
                En
              </span>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default Footer;
